/**
 * ComuneAutocomplete — ISTAT-backed Italian municipality search.
 * Calls /api/comuni/search with debounce, shows dropdown with prov+regione.
 * onChange callback receives the full comune object (or null on clear).
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, Search, X, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ComuneAutocomplete = ({ value, onChange, placeholder = "Cerca comune..." }) => {
  const [query, setQuery] = useState(value?.nome || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value?.nome || ""); }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const doSearch = (q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/comuni/search?q=${encodeURIComponent(q)}&limit=10`);
        const data = await res.json();
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
  };

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    doSearch(v);
    // If user clears the input → also clear selection
    if (!v.trim() && value) onChange(null);
  };

  const handleSelect = (c) => {
    setQuery(c.nome);
    setOpen(false);
    onChange(c);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    onChange(null);
  };

  return (
    <div className="relative" ref={containerRef} data-testid="comune-autocomplete">
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { setOpen(true); if (!results.length) doSearch(query); }}
          placeholder={placeholder}
          className="w-full bg-white border border-zinc-200 rounded-2xl pl-11 pr-11 py-3 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/20 font-medium"
          data-testid="comune-input"
          autoComplete="off"
        />
        {query && (
          <button type="button" onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center"
            data-testid="comune-clear">
            <X className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        )}
      </div>

      {value && !open && (
        <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5" data-testid="comune-selected-info">
          <Check className="w-3 h-3 text-emerald-600" />
          <span className="font-semibold">{value.provincia}</span>
          <span>·</span>
          <span>{value.regione}</span>
        </p>
      )}

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-zinc-200 max-h-80 overflow-y-auto" data-testid="comune-dropdown">
          {loading && (
            <div className="px-4 py-3 text-sm text-zinc-400 flex items-center gap-2">
              <Search className="w-4 h-4 animate-pulse" /> Ricerca...
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-zinc-400">Nessun comune trovato</div>
          )}
          {!loading && results.map((c) => (
            <button
              key={c.istat}
              type="button"
              onClick={() => handleSelect(c)}
              className="w-full text-left px-4 py-2.5 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
              data-testid={`comune-option-${c.istat}`}
            >
              <p className="text-sm font-bold text-zinc-900 truncate">{c.nome}</p>
              <p className="text-xs text-zinc-400 truncate">{c.provincia} ({c.sigla}) · {c.regione}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ComuneAutocomplete;
